import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Phone, ArrowRightLeft, FileText, BedDouble, Cross, LogOut as LogOutIcon, Paperclip, Printer, Download, Upload as UploadIcon, FileUp, User, Users } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { SERVICES, SERVICE_LABEL, LANG_LABEL } from "@/lib/labels";

export default function Patients() {
  const { t, user } = useApp();
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    phone: "", name: "", age: 18, category: "general", address: "", language: "fr", notes: "", service: "general",
    commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC",
  });

  // Transfer
  const [trOpen, setTrOpen] = useState(false);
  const [trPatient, setTrPatient] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [trForm, setTrForm] = useState({ target_hospital_id: "", consent: false, reason: "" });

  // Notes
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesPatient, setNotesPatient] = useState(null);
  const [noteContent, setNoteContent] = useState("");

  // Attachments
  const [attOpen, setAttOpen] = useState(false);
  const [attPatient, setAttPatient] = useState(null);
  const [attDescription, setAttDescription] = useState("");
  const [attUploading, setAttUploading] = useState(false);
  const attFileRef = React.useRef(null);

  // CSV Import
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvFileRef = React.useRef(null);

  const importCsv = async () => {
    const file = csvFileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier CSV"); return; }
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/patients/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(r.data);
      toast.success(`${r.data.imported} patient(s) importé(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally { setCsvUploading(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,phone,category,language,age,commune,quartier,avenue,ville,pays\nMme KABEYA Marie,+243999123456,pregnant,ln,28,Gombe,Q.7,Av. Kasa-Vubu 123,Kinshasa,RDC\nEnfant NGOY,+243999654321,child,fr,2,Kalamu,,,,\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "kwikilacare_import_patients_template.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const role = user?.role;
  const isAdmin = role === "hospital_admin";
  const isDoctor = role === "doctor";
  const isNurse = role === "nurse";
  const isSecretary = role === "secretary";
  const [mine, setMine] = useState(isDoctor); // doctors default to "mes patients"

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (filter !== "all") params.category = filter;
    if (search.trim()) params.search = search.trim();
    if (mine) params.mine = true;
    const res = await axios.get(`${API}/patients`, { params });
    const data = res.data;
    if (Array.isArray(data)) {
      // backward compatibility (should not happen)
      setPatients(data); setTotal(data.length); setPages(1);
    } else {
      setPatients(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page, mine]);
  // Debounced search reset
  useEffect(() => {
    const id = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients`, { ...form, age: parseInt(form.age) });
      toast.success("Patient enregistré + SMS bienvenue (MOCK)");
      setOpen(false);
      setForm({
        phone: "", name: "", age: 18, category: "general", address: "", language: "fr", notes: "", service: "general",
        commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce patient ?")) return;
    await axios.delete(`${API}/patients/${id}`);
    toast.success("Patient supprimé");
    load();
  };

  // Hospitalization & death actions
  const [hospOpen, setHospOpen] = useState(false);
  const [hospPatient, setHospPatient] = useState(null);
  const [hospForm, setHospForm] = useState({ room_number: "", admission_reason: "" });
  const [deathOpen, setDeathOpen] = useState(false);
  const [deathPatient, setDeathPatient] = useState(null);
  const [deathForm, setDeathForm] = useState({ death_cause: "", notes: "" });

  const openHospitalize = (p) => {
    setHospPatient(p);
    setHospForm({ room_number: "", admission_reason: "" });
    setHospOpen(true);
  };
  const submitHospitalize = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients/${hospPatient.id}/hospitalize`, hospForm);
      toast.success(`${hospPatient.name} hospitalisé(e)`);
      setHospOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const dischargePatient = async (p) => {
    if (!window.confirm(`Décharger ${p.name} ?`)) return;
    try {
      await axios.post(`${API}/patients/${p.id}/discharge`);
      toast.success("Patient déchargé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openDeath = (p) => {
    setDeathPatient(p);
    setDeathForm({ death_cause: "", notes: "" });
    setDeathOpen(true);
  };
  const submitDeath = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Confirmer le décès de ${deathPatient.name} ? Cette action sera enregistrée dans le registre médical.`)) return;
    try {
      await axios.post(`${API}/patients/${deathPatient.id}/deceased`, deathForm);
      toast.success("Décès enregistré");
      setDeathOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openTransfer = async (p) => {
    setTrPatient(p);
    setTrForm({ target_hospital_id: "", consent: false, reason: "" });
    try {
      // hospital_admin doesn't have access to /api/hospitals (super_admin only).
      // So we let user paste hospital_id manually OR use input.
      // We'll just use a text input.
      setHospitals([]);
    } catch {}
    setTrOpen(true);
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!trForm.consent) { toast.error("Consentement requis"); return; }
    try {
      await axios.post(`${API}/patients/${trPatient.id}/transfer`, trForm);
      toast.success("Patient transféré");
      setTrOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur transfert");
    }
  };

  const openNotes = async (p) => {
    const r = await axios.get(`${API}/patients/${p.id}`);
    setNotesPatient(r.data);
    setNoteContent("");
    setNotesOpen(true);
  };

  const addNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/patients/${notesPatient.id}/notes`, { content: noteContent });
      const r = await axios.get(`${API}/patients/${notesPatient.id}`);
      setNotesPatient(r.data);
      setNoteContent("");
      toast.success("Note ajoutée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // ------- Attachments -------
  const openAttachments = async (p) => {
    try {
      const r = await axios.get(`${API}/patients/${p.id}`);
      setAttPatient(r.data);
      setAttDescription("");
      if (attFileRef.current) attFileRef.current.value = "";
      setAttOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const uploadAttachment = async () => {
    const file = attFileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier > 10 MB"); return; }
    setAttUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", attDescription);
      await axios.post(`${API}/patients/${attPatient.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Pièce jointe ajoutée");
      const r = await axios.get(`${API}/patients/${attPatient.id}`);
      setAttPatient(r.data);
      setAttDescription("");
      if (attFileRef.current) attFileRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally { setAttUploading(false); }
  };

  const downloadAttachment = async (aid, filename) => {
    try {
      const r = await axios.get(`${API}/patients/${attPatient.id}/attachments/${aid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename || "document";
      document.body.appendChild(link); link.click();
      link.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error("Téléchargement impossible"); }
  };

  const deleteAttachment = async (aid) => {
    if (!window.confirm("Supprimer cette pièce jointe ?")) return;
    try {
      await axios.delete(`${API}/patients/${attPatient.id}/attachments/${aid}`);
      const r = await axios.get(`${API}/patients/${attPatient.id}`);
      setAttPatient(r.data);
      toast.success("Pièce jointe supprimée");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = patients;

  const catLabel = (c) => ({ pregnant: t.dash.pregnant, child: t.dash.child, elderly: t.dash.elderly, general: t.dash.general }[c]);
  const catColor = (c) => ({ pregnant: "#D96C4A", child: "#2E7D32", elderly: "#1E4631", general: "#5C6B61" }[c]);

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.patients}</h1>
        <div className="flex gap-2 flex-wrap items-center">
        {/* Mine / All scope toggle */}
        <div className="inline-flex bg-white border border-[#E5EBE7] rounded-lg p-1" data-testid="scope-toggle">
          <button onClick={()=>{ setPage(1); setMine(true); }} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${mine ? "bg-[#D96C4A] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="scope-mine">
            <User className="w-3.5 h-3.5"/> Mes patients
          </button>
          <button onClick={()=>{ setPage(1); setMine(false); }} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${!mine ? "bg-[#D96C4A] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="scope-all">
            <Users className="w-3.5 h-3.5"/> Tout l&apos;hôpital
          </button>
        </div>
        {(isAdmin || isSecretary || isDoctor || isNurse) && (
          <Button variant="outline" onClick={() => { setCsvOpen(true); setCsvResult(null); if (csvFileRef.current) csvFileRef.current.value = ""; }} data-testid="import-csv-btn">
            <FileUp className="w-4 h-4 mr-2"/> Importer CSV
          </Button>
        )}
        {(isAdmin || isSecretary || isDoctor || isNurse) && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="add-patient-btn">
                <Plus className="w-4 h-4 mr-2"/> {t.dash.add_patient}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t.dash.add_patient}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="patient-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t.dash.name}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="form-name"/></div>
                  <div><Label>{t.dash.phone}</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} placeholder="+243..." required data-testid="form-phone"/></div>
                  <div><Label>{t.dash.age}</Label><Input type="number" value={form.age} onChange={(e)=>setForm({...form, age: e.target.value})} required data-testid="form-age"/></div>
                  <div>
                    <Label>{t.dash.category}</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                      <SelectTrigger data-testid="form-category"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pregnant">{t.dash.pregnant}</SelectItem>
                        <SelectItem value="child">{t.dash.child}</SelectItem>
                        <SelectItem value="elderly">{t.dash.elderly}</SelectItem>
                        <SelectItem value="general">{t.dash.general}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t.dash.language}</Label>
                    <Select value={form.language} onValueChange={(v)=>setForm({...form, language: v})}>
                      <SelectTrigger data-testid="form-language"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="ln">Lingala</SelectItem>
                        <SelectItem value="sw">Swahili</SelectItem>
                        <SelectItem value="ts">Tshiluba</SelectItem>
                        <SelectItem value="kg">Kikongo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>{t.dash.service}</Label>
                    <Select value={form.service} onValueChange={(v)=>setForm({...form, service: v})}>
                      <SelectTrigger data-testid="form-service"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((sv) => <SelectItem key={sv.id} value={sv.id}>{sv.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>{t.dash.address} (libre)</Label><Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} placeholder="Description libre ou contact tuteur" data-testid="form-address"/></div>
                  <div className="col-span-2 border-t border-[#E5EBE7] pt-3 mt-1">
                    <div className="text-xs font-semibold uppercase text-[#5C6B61] mb-2">Adresse physique structurée</div>
                  </div>
                  <div><Label>Commune</Label><Input value={form.commune} onChange={(e)=>setForm({...form, commune: e.target.value})} placeholder="Ex: Gombe, Kalamu…" data-testid="form-commune"/></div>
                  <div><Label>Quartier</Label><Input value={form.quartier} onChange={(e)=>setForm({...form, quartier: e.target.value})} placeholder="Ex: Lemba Q.7" data-testid="form-quartier"/></div>
                  <div className="col-span-2"><Label>Avenue et N°</Label><Input value={form.avenue} onChange={(e)=>setForm({...form, avenue: e.target.value})} placeholder="Ex: Av. Kasa-Vubu N°123" data-testid="form-avenue"/></div>
                  <div><Label>Ville</Label><Input value={form.ville} onChange={(e)=>setForm({...form, ville: e.target.value})} data-testid="form-ville"/></div>
                  <div><Label>Pays</Label><Input value={form.pays} onChange={(e)=>setForm({...form, pays: e.target.value})} data-testid="form-pays"/></div>
                  <div className="col-span-2"><Label>{t.dash.notes}</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="form-notes"/></div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={()=>setOpen(false)}>{t.dash.cancel}</Button>
                  <Button type="submit" className="bg-[#1E4631] hover:bg-[#163424]" data-testid="form-save">{t.dash.save}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* CSV Import Dialog */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-lg" data-testid="csv-import-dialog">
          <DialogHeader><DialogTitle className="inline-flex items-center gap-2"><FileUp className="w-5 h-5"/> Importer des patients depuis un CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#1E4631]/5 border border-[#1E4631]/20 rounded-lg text-xs text-[#5C6B61]">
              <div className="font-semibold text-[#1E4631] mb-1">Format attendu (colonnes)</div>
              <div>Requises : <code>name</code>, <code>phone</code>, <code>category</code> (pregnant / child / elderly / general)</div>
              <div>Optionnelles : <code>language</code> (fr/ln/sw/ts/kg), <code>age</code>, <code>commune</code>, <code>quartier</code>, <code>avenue</code>, <code>ville</code>, <code>pays</code>, <code>notes</code></div>
              <div className="mt-2">Encodage UTF-8 · séparateur virgule · max 5 MB</div>
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="w-full" data-testid="download-template">
              <Download className="w-4 h-4 mr-2"/> Télécharger le modèle CSV
            </Button>
            <input ref={csvFileRef} type="file" accept=".csv,text/csv" className="text-sm block w-full file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-[#1E4631] file:text-[#1E4631] file:bg-white hover:file:bg-[#1E4631]/5 file:cursor-pointer" data-testid="csv-file-input"/>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCsvOpen(false)}>Fermer</Button>
              <Button onClick={importCsv} disabled={csvUploading} className="bg-[#1E4631] hover:bg-[#163424]" data-testid="csv-upload-btn">
                <UploadIcon className="w-4 h-4 mr-2"/>
                {csvUploading ? "Import en cours…" : "Importer"}
              </Button>
            </div>
            {csvResult && (
              <div className="p-3 rounded-lg bg-[#FDFBF7] border border-[#E5EBE7] text-sm" data-testid="csv-result">
                <div className="font-semibold text-[#2E7D32]">✅ {csvResult.imported} patient(s) importé(s)</div>
                {csvResult.errors_count > 0 && (
                  <div className="mt-2">
                    <div className="text-[#C62828] font-medium">⚠️ {csvResult.errors_count} ligne(s) rejetée(s)</div>
                    <ul className="text-xs mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                      {csvResult.errors.map((e, i) => (
                        <li key={i} className="text-[#5C6B61]">Ligne {e.line} : {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border border-[#E5EBE7]">
        <Input placeholder={t.dash.search} value={search} onChange={(e)=>setSearch(e.target.value)} className="max-w-xs" data-testid="search-input"/>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.dash.all_categories}</SelectItem>
            <SelectItem value="pregnant">{t.dash.pregnant}</SelectItem>
            <SelectItem value="child">{t.dash.child}</SelectItem>
            <SelectItem value="elderly">{t.dash.elderly}</SelectItem>
            <SelectItem value="general">{t.dash.general}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#5C6B61] ml-auto">{total} patient(s)</span>
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61] tracking-wider">
            <tr>
              <th className="px-5 py-3">{t.dash.name}</th>
              <th className="px-5 py-3">{t.dash.phone}</th>
              <th className="px-5 py-3">{t.dash.age}</th>
              <th className="px-5 py-3">{t.dash.category}</th>
              <th className="px-5 py-3">{t.dash.language}</th>
              <th className="px-5 py-3">{t.dash.actions}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-12 text-[#5C6B61]" data-testid="empty-patients">Aucun patient</td></tr>
            ) : filtered.map((p) => {
              const status = p.status || "external";
              const isHosp = status === "hospitalized";
              const isDeceased = status === "deceased";
              return (
              <tr key={p.id} className={`border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50 ${isDeceased ? "opacity-60 italic" : ""}`} data-testid={`patient-row-${p.id}`}>
                <td className="px-5 py-3 font-medium">
                  <span className="inline-flex items-center gap-2">
                    {p.name}
                    {isHosp && <span title={`Chambre ${p.room_number || '—'}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D96C4A]/15 text-[#D96C4A] font-bold">Hospitalisé</span>}
                    {isDeceased && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C62828]/15 text-[#C62828] font-bold">Décédé</span>}
                  </span>
                </td>
                <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#5C6B61]"/>{p.phone}</span></td>
                <td className="px-5 py-3">{p.age}</td>
                <td className="px-5 py-3">
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: `${catColor(p.category)}15`, color: catColor(p.category) }}>
                    {catLabel(p.category)}
                  </span>
                  {p.service && p.service !== "general" && (
                    <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#2E5C7E]/10 text-[#2E5C7E]" data-testid={`service-${p.id}`}>{SERVICE_LABEL[p.service]}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-xs">{LANG_LABEL[p.language] || p.language}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1.5">
                    {(isDoctor || isAdmin) && !isDeceased && (
                      <button onClick={()=>openNotes(p)} className="text-[#1E4631] hover:bg-[#1E4631]/10 p-1.5 rounded transition" title="Notes médicales" data-testid={`notes-${p.id}`}>
                        <FileText className="w-4 h-4"/>
                      </button>
                    )}
                    <button onClick={()=>openAttachments(p)} className="text-[#1E4631] hover:bg-[#1E4631]/10 p-1.5 rounded transition" title="Pièces jointes" data-testid={`attachments-${p.id}`}>
                      <Paperclip className="w-4 h-4"/>
                    </button>
                    <Link to={`/dashboard/patients/${p.id}/print`} target="_blank" rel="noopener" className="text-[#1E4631] hover:bg-[#1E4631]/10 p-1.5 rounded transition inline-flex items-center" title="Fiche imprimable" data-testid={`print-${p.id}`}>
                      <Printer className="w-4 h-4"/>
                    </Link>
                    {(isAdmin || isDoctor || isNurse) && !isDeceased && (
                      isHosp ? (
                        <button onClick={()=>dischargePatient(p)} className="text-[#2E7D32] hover:bg-[#2E7D32]/10 p-1.5 rounded transition" title="Décharger" data-testid={`discharge-${p.id}`}>
                          <LogOutIcon className="w-4 h-4"/>
                        </button>
                      ) : (
                        <button onClick={()=>openHospitalize(p)} className="text-[#D96C4A] hover:bg-[#D96C4A]/10 p-1.5 rounded transition" title="Hospitaliser" data-testid={`hospitalize-${p.id}`}>
                          <BedDouble className="w-4 h-4"/>
                        </button>
                      )
                    )}
                    {(isAdmin || isDoctor) && !isDeceased && (
                      <button onClick={()=>openDeath(p)} className="text-[#C62828] hover:bg-[#C62828]/10 p-1.5 rounded transition" title="Enregistrer décès" data-testid={`death-${p.id}`}>
                        <Cross className="w-4 h-4"/>
                      </button>
                    )}
                    {isAdmin && !isDeceased && (
                      <button onClick={()=>openTransfer(p)} className="text-[#D96C4A] hover:bg-[#D96C4A]/10 p-1.5 rounded transition" title="Transférer" data-testid={`transfer-${p.id}`}>
                        <ArrowRightLeft className="w-4 h-4"/>
                      </button>
                    )}
                    {(isAdmin || isSecretary) && (
                      <button onClick={()=>onDelete(p.id)} className="text-[#C62828] hover:bg-red-50 p-1.5 rounded transition" data-testid={`delete-${p.id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-[#E5EBE7]" data-testid="patients-pagination">
          <div className="text-xs text-[#5C6B61]">Page {page} / {pages} · {total} patient(s)</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={()=>setPage(p=>Math.max(1,p-1))} data-testid="page-prev">Précédent</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={()=>setPage(p=>Math.min(pages,p+1))} data-testid="page-next">Suivant</Button>
          </div>
        </div>
      )}

      {/* Hospitalize Dialog */}
      <Dialog open={hospOpen} onOpenChange={setHospOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hospitaliser le patient</DialogTitle></DialogHeader>
          <form onSubmit={submitHospitalize} className="space-y-4" data-testid="hospitalize-form">
            {hospPatient && <div className="text-sm text-[#5C6B61]">Patient : <strong className="text-[#1A1D1A]">{hospPatient.name}</strong> · {hospPatient.age} ans · {hospPatient.phone}</div>}
            <div><Label>N° Chambre</Label><Input value={hospForm.room_number} onChange={(e)=>setHospForm({...hospForm, room_number: e.target.value})} placeholder="Ex: 205, A-12…" data-testid="hosp-room"/></div>
            <div><Label>Motif d'admission</Label><Input required value={hospForm.admission_reason} onChange={(e)=>setHospForm({...hospForm, admission_reason: e.target.value})} placeholder="Ex: Suivi prénatal intensif, intervention chirurgicale…" data-testid="hosp-reason"/></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setHospOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#D96C4A] hover:bg-[#B85A3D]" data-testid="hosp-submit">
                <BedDouble className="w-4 h-4 mr-2"/> Hospitaliser
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Death Dialog */}
      <Dialog open={deathOpen} onOpenChange={setDeathOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#C62828]">Enregistrer un décès</DialogTitle></DialogHeader>
          <form onSubmit={submitDeath} className="space-y-4" data-testid="death-form">
            {deathPatient && <div className="text-sm text-[#5C6B61]">Patient : <strong className="text-[#1A1D1A]">{deathPatient.name}</strong> · {deathPatient.age} ans</div>}
            <div className="p-3 bg-[#C62828]/10 border border-[#C62828]/30 rounded-lg text-xs text-[#C62828]">
              ⚠️ Cette action enregistre le décès dans le registre médical et est journalisée dans l'audit. Action irréversible.
            </div>
            <div><Label>Cause du décès</Label><Input required value={deathForm.death_cause} onChange={(e)=>setDeathForm({...deathForm, death_cause: e.target.value})} placeholder="Ex: Complications post-natales, infarctus…" data-testid="death-cause"/></div>
            <div><Label>Notes complémentaires (optionnel)</Label><Input value={deathForm.notes} onChange={(e)=>setDeathForm({...deathForm, notes: e.target.value})} data-testid="death-notes"/></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setDeathOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#C62828] hover:bg-[#A41818]" data-testid="death-submit">
                <Cross className="w-4 h-4 mr-2"/> Enregistrer le décès
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={trOpen} onOpenChange={setTrOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t.dash.transfer_title}</DialogTitle></DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-4" data-testid="transfer-form">
            {trPatient && <div className="text-sm text-[#5C6B61]">Patient : <strong className="text-[#1A1D1A]">{trPatient.name}</strong> · {trPatient.phone}</div>}
            <div>
              <Label>ID Hôpital cible</Label>
              <Input required value={trForm.target_hospital_id} onChange={(e)=>setTrForm({...trForm, target_hospital_id: e.target.value})} placeholder="UUID de l'hôpital partenaire" data-testid="transfer-target"/>
              <div className="text-xs text-[#5C6B61] mt-1">Demandez l'ID à l'hôpital cible ou à l'équipe KWIKILA.</div>
            </div>
            <div><Label>{t.dash.transfer_reason}</Label><Input value={trForm.reason} onChange={(e)=>setTrForm({...trForm, reason: e.target.value})} data-testid="transfer-reason"/></div>
            <div className="flex items-start gap-2 p-3 bg-[#E3A72F]/10 rounded-lg border border-[#E3A72F]/30">
              <Checkbox id="consent" checked={trForm.consent} onCheckedChange={(v)=>setTrForm({...trForm, consent: !!v})} data-testid="transfer-consent"/>
              <label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">{t.dash.consent}</label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setTrOpen(false)}>{t.dash.cancel}</Button>
              <Button type="submit" className="bg-[#D96C4A] hover:bg-[#B85A3D]" data-testid="transfer-submit">{t.dash.transfer}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t.dash.medical_notes} — {notesPatient?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto" data-testid="notes-list">
            {notesPatient?.medical_notes?.length ? notesPatient.medical_notes.map((n) => (
              <div key={n.id} className="p-4 rounded-lg bg-[#FDFBF7] border border-[#E5EBE7]">
                <div className="text-xs text-[#5C6B61] mb-1">Par <strong>{n.by}</strong> · {new Date(n.at).toLocaleString("fr-FR")}</div>
                <div className="text-sm whitespace-pre-wrap">{n.content}</div>
              </div>
            )) : <div className="text-sm text-[#5C6B61] text-center py-6">Aucune note</div>}
          </div>
          {isDoctor && (
            <form onSubmit={addNote} className="space-y-3 border-t border-[#E5EBE7] pt-4" data-testid="add-note-form">
              <Label>{t.dash.note_content}</Label>
              <Textarea value={noteContent} onChange={(e)=>setNoteContent(e.target.value)} required rows={3} data-testid="note-content"/>
              <Button type="submit" className="bg-[#1E4631]" data-testid="note-save">{t.dash.add_note}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Attachments Dialog */}
      <Dialog open={attOpen} onOpenChange={setAttOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="inline-flex items-center gap-2"><Paperclip className="w-5 h-5"/> Pièces jointes — {attPatient?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto" data-testid="attachments-list">
            {attPatient?.attachments?.length ? attPatient.attachments.map((a) => (
              <div key={a.id} className="p-3 rounded-lg bg-[#FDFBF7] border border-[#E5EBE7] flex items-start justify-between gap-3" data-testid={`attachment-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{a.filename}</div>
                  <div className="text-xs text-[#5C6B61] mt-0.5">
                    {(a.content_type || "").split("/")[1] || "fichier"} · {Math.round((a.size || 0) / 1024)} Ko · par {a.uploaded_by} · {new Date(a.uploaded_at).toLocaleString("fr-FR")}
                  </div>
                  {a.description && <div className="text-xs italic text-[#5C6B61] mt-1">{a.description}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={()=>downloadAttachment(a.id, a.filename)} className="text-[#1E4631] hover:bg-[#1E4631]/10 p-1.5 rounded transition" title="Télécharger" data-testid={`download-att-${a.id}`}>
                    <Download className="w-4 h-4"/>
                  </button>
                  {(isAdmin || isDoctor) && (
                    <button onClick={()=>deleteAttachment(a.id)} className="text-[#C62828] hover:bg-red-50 p-1.5 rounded transition" title="Supprimer" data-testid={`delete-att-${a.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              </div>
            )) : <div className="text-sm text-[#5C6B61] text-center py-4">Aucune pièce jointe pour ce patient.</div>}
          </div>
          <div className="border-t border-[#E5EBE7] pt-4 space-y-3">
            <div className="text-xs text-[#5C6B61]">
              {(attPatient?.attachments?.length || 0)}/10 fichier(s) · PDF ou images (jpg, png, webp…) · 10 MB max
            </div>
            <input ref={attFileRef} type="file" accept="application/pdf,image/*" className="text-sm block w-full file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-[#1E4631] file:text-[#1E4631] file:bg-white hover:file:bg-[#1E4631]/5 file:cursor-pointer" data-testid="att-file-input"/>
            <Input placeholder="Description (facultatif)" value={attDescription} onChange={(e)=>setAttDescription(e.target.value)} maxLength={300} data-testid="att-description"/>
            <div className="flex justify-end">
              <Button
                onClick={uploadAttachment}
                disabled={attUploading || (attPatient?.attachments?.length || 0) >= 10}
                className="bg-[#1E4631] hover:bg-[#163424]"
                data-testid="att-upload"
              >
                <UploadIcon className="w-4 h-4 mr-2"/>
                {attUploading ? "Envoi…" : "Ajouter"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
